import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import useVPSMonitoring from "./Server/hooks/useVPSMonitoring";
import Overview from "./Server/components/Overview";
import FileManager from "./Server/components/FileManager";
import CleanupActions from "./Server/components/CleanupActions";
import VPSActionHistory from "./Server/components/VPSActionHistory";

const TABS = ["overview", "files", "maintenance", "history"];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "20px 24px 32px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minHeight: "100%",
    boxSizing: "border-box",
    [theme.breakpoints.down("xs")]: { padding: "16px 12px 24px" },
  },
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: theme.palette.text.primary },
  subtitle: { margin: "4px 0 0", fontSize: 14, color: theme.palette.text.secondary },
  actions: { display: "flex", gap: 8, alignItems: "center" },
  action: { textTransform: "none", fontWeight: 600, borderRadius: 8 },
  tabs: { borderBottom: `1px solid ${theme.palette.divider}`, minHeight: 40 },
  tab: { textTransform: "none", fontWeight: 600, minHeight: 40, minWidth: 90 },
}));

/**
 * «Estado de la plataforma» (doc 0447, D6c): el servidor, solo para el superadministrador.
 * «Conexiones» y «Aplicación» se retiraron (cifras falsas o repetidas). La pestaña va en
 * `?tab=` para poder enlazarla.
 */
const Platform = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const asked = new URLSearchParams(location.search).get("tab");
  const tab = TABS.includes(asked) ? asked : TABS[0];

  const {
    loading,
    error,
    systemStatus,
    fileAnalysis,
    history: samples,
    thresholds,
    databaseActivity,
    autoRefresh,
    forceRefresh,
    fetchHistory,
    setAutoRefresh,
    executeCleanup,
  } = useVPSMonitoring();

  const changeTab = (_, value) => history.replace({ pathname: location.pathname, search: `?tab=${value}` });

  const refreshNow = async () => {
    await forceRefresh();
    fetchHistory(7);
    toast.success(i18n.t("platform.refreshed"));
  };

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <div>
          <h1 className={classes.title}>{i18n.t("platform.title")}</h1>
          <p className={classes.subtitle}>{i18n.t("platform.subtitle")}</p>
        </div>
        <div className={classes.actions}>
          <Tooltip title={i18n.t("platform.autoRefreshHint")}>
            <Button
              size="small"
              variant={autoRefresh ? "contained" : "outlined"}
              color="primary"
              disableElevation
              className={classes.action}
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              {autoRefresh ? i18n.t("platform.autoOn") : i18n.t("platform.autoOff")}
            </Button>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            className={classes.action}
            startIcon={<RefreshIcon />}
            onClick={refreshNow}
            disabled={loading}
          >
            {i18n.t("platform.refresh")}
          </Button>
        </div>
      </header>

      <Tabs
        value={tab}
        onChange={changeTab}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        className={classes.tabs}
      >
        {TABS.map((t) => (
          <Tab key={t} value={t} label={i18n.t(`platform.tabs.${t}`)} className={classes.tab} />
        ))}
      </Tabs>

      {tab === "overview" && (
        <Overview
          loading={loading}
          error={error}
          systemStatus={systemStatus}
          thresholds={thresholds}
          databaseActivity={databaseActivity}
          history={samples}
          onFetchHistory={fetchHistory}
        />
      )}
      {tab === "files" && <FileManager loading={loading} error={error} fileAnalysis={fileAnalysis} onRefresh={forceRefresh} />}
      {tab === "maintenance" && <CleanupActions loading={loading} error={error} onAction={executeCleanup} />}
      {tab === "history" && <VPSActionHistory />}
    </div>
  );
};

export default Platform;
